import { REQUIRED_STAGES } from './profiles.js';
import { validBehaviorsForStage } from './stages.js';
import { tokenizeXPath } from './header-paths/xml-grammar.js';
import { tokenizeJsonPath } from './header-paths/json-grammar.js';
import { validateRuleCannotFire } from './validate-rule-cannot-fire.js';
/**
 * The only place `linkTarget.tab` values are hand-enumerated. `tab` below is
 * DERIVED from this array (`(typeof LINK_TARGET_TABS)[number]`), not
 * independently typed, so the type and the runtime list can never drift from
 * EACH OTHER. They can still drift from the real web app (a FormConfigPage
 * tab gets renamed/removed) — that is guarded at test time, not compile
 * time, by `packages/shared/src/form-config/__tests__/link-target-tab.compat.test.ts`,
 * which reads apps/web/src/features/admin/form-config/FormConfigPage.tsx's
 * `Tab` union and `RELOCATED_TABS` map (plus JobTypeDetailPage.tsx's
 * `STALE_TABS`) directly at test run time — packages/shared cannot import
 * apps/web (wrong dependency direction), so a compile-time subset check
 * against the real union isn't possible; the regex-derived runtime check is
 * the closest equivalent and self-updates instead of going stale.
 *
 * Before adding a value here, confirm it is really an id in FormConfigPage's
 * `Tab` union (or reachable via its `RELOCATED_TABS` redirect) — a value
 * that isn't renders a PERMANENTLY BLANK panel when clicked (FormConfigPage's
 * `params.get('tab') as Tab ?? 'fields'` only defaults on `null`, not on an
 * unrecognized string). This is exactly the shape of the 2026-08-09 T3
 * review finding — `'export'` lived in this union long after the real tab
 * was renamed to `'export-mapping'`, undetected because nothing checked it
 * against the live web app.
 */
export const LINK_TARGET_TABS = [
    'fields', 'header', 'pipeline', 'rules', 'export-mapping', 'history', 'sample', 'zones',
];
const registered = [];
export function registerValidator(fn) {
    registered.push(fn);
}
export function __resetValidators() {
    registered.length = 0;
}
export function validateContent(content, opts = {}) {
    const deep = opts.deep ?? false;
    const out = [];
    // field.name.duplicate + field.name.empty
    const seen = new Map();
    for (const f of content.fields) {
        if (!f.name || f.name.trim() === '') {
            out.push({
                code: 'field.name.empty', severity: 'error',
                path: ['fields', f.fieldDefinitionId], message: 'Field name is required',
                fieldName: f.name,
            });
            continue;
        }
        const prev = seen.get(f.name);
        if (prev !== undefined) {
            out.push({
                code: 'field.name.duplicate', severity: 'error',
                path: ['fields', f.name], message: `Duplicate field name "${f.name}"`,
                fieldName: f.name,
            });
        }
        seen.set(f.name, (prev ?? 0) + 1);
    }
    // R-156 — Label publish-gate: defence-in-depth against the "every field
    // reads 'New Field' in the Verification Station" data-integrity bug.
    //
    // The pre-R-141 `FieldActions.handleAdd` seeded `label: 'New Field'` into
    // FieldContent and the drawer accepted that placeholder unchanged. R-141
    // closed the *input* path (FE now seeds `label: ''` and the drawer
    // rejects empty/`'New Field'` on blur), but published versions corrupted
    // before that fix landed (e.g. DE 1545 v2 — 32/35 labels were the literal
    // placeholder) had no way to be re-published cleanly because nothing on
    // the publish path actually inspected labels. Any future regression in
    // the FE — or a hand-edited / cherry-picked / restored content — would
    // silently slip through the same way.
    //
    // Empty labels and the literal "New Field" placeholder are blocked here
    // as `error` severity so `publishDraft` raises `VALIDATION_FAILED` and
    // the operator must set a real label before the version goes live. We
    // only block what we know is wrong — anything else (operator-typed
    // shorthand, multi-word labels, etc.) passes through.
    for (const f of content.fields) {
        const labelRaw = typeof f.label === 'string' ? f.label : '';
        const labelTrim = labelRaw.trim();
        if (labelTrim === '') {
            out.push({
                code: 'field.label.empty',
                severity: 'error',
                path: ['fields', f.name, 'label'],
                message: `Field "${f.name}" has no label — set a human-readable label before publishing.`,
                fieldName: f.name,
                linkTarget: { tab: 'fields', fieldName: f.name },
            });
        }
        else if (labelTrim === 'New Field') {
            out.push({
                code: 'field.label.placeholder',
                severity: 'error',
                path: ['fields', f.name, 'label'],
                message: `Field "${f.name}" still uses the "New Field" placeholder — set a real label before publishing.`,
                fieldName: f.name,
                linkTarget: { tab: 'fields', fieldName: f.name },
            });
        }
    }
    const fieldNames = new Set(content.fields.map((f) => f.name));
    // behavior.stage.invalid + per-behavior refs + unused-field warn
    for (const f of content.fields) {
        const stageKeys = Object.keys(f.stageMap);
        let behaviorCount = 0;
        for (const stage of stageKeys) {
            const allowed = validBehaviorsForStage(stage);
            const behaviors = f.stageMap[stage] ?? [];
            behaviorCount += behaviors.length;
            behaviors.forEach((b, idx) => {
                if (!allowed.includes(b.kind)) {
                    out.push({
                        code: 'behavior.stage.invalid', severity: 'error',
                        path: ['fields', f.name, 'stageMap', stage, String(idx)],
                        message: `${b.kind} is not valid at stage ${stage}`,
                        fieldName: f.name,
                    });
                }
                checkBehaviorRefs(b, f, stage, idx, fieldNames, out);
                checkBehaviorCompleteness(b, f, stage, idx, out);
            });
        }
        if (behaviorCount === 0) {
            out.push({
                code: 'field.unused', severity: 'warn',
                path: ['fields', f.name], message: `Field "${f.name}" has no behaviors`,
                fieldName: f.name,
            });
        }
    }
    // R-17 — conditionalVisibility publish-gate guards. A reference to a
    // non-existent field is silently a no-op at the verifier (the visibility
    // predicate just never resolves), so a broken reference must be surfaced
    // at publish rather than discovered by an operator. Self-reference is
    // similarly caught — the editor UI already prevents it, but a hand-edited
    // or imported config can slip through without this gate.
    for (const f of content.fields) {
        const cv = f.conditionalVisibility;
        if (!cv)
            continue;
        if (cv.field === f.name) {
            out.push({
                code: 'conditionalVisibility.field.self',
                severity: 'error',
                path: ['fields', f.name, 'conditionalVisibility', 'field'],
                message: `Field "${f.name}" references itself in conditionalVisibility`,
                fieldName: f.name,
            });
        }
        else if (!fieldNames.has(cv.field)) {
            out.push({
                code: 'conditionalVisibility.field.missing',
                severity: 'error',
                path: ['fields', f.name, 'conditionalVisibility', 'field'],
                message: `Field "${f.name}" conditionalVisibility references field "${cv.field}" which is not on this form`,
                fieldName: f.name,
            });
        }
    }
    // emit.destination.unknown
    const destIds = new Set(content.export.destinations.map((d) => d.id));
    for (const f of content.fields) {
        for (const stage of Object.keys(f.stageMap)) {
            (f.stageMap[stage] ?? []).forEach((b, idx) => {
                if (b.kind === 'emit' && Array.isArray(b.destinations)) {
                    for (const id of b.destinations) {
                        if (!destIds.has(id)) {
                            out.push({
                                code: 'emit.destination.unknown', severity: 'error',
                                path: ['fields', f.name, 'stageMap', stage, String(idx), 'destinations'],
                                message: `Destination "${id}" is not defined on this form`,
                                fieldName: f.name,
                            });
                        }
                    }
                }
            });
        }
    }
    // destination.id.duplicate
    const seenDestIds = new Map();
    for (const d of content.export.destinations) {
        const prev = seenDestIds.get(d.id);
        if (prev !== undefined) {
            out.push({
                code: 'destination.id.duplicate',
                severity: 'error',
                path: ['export', 'destinations', d.id],
                message: `Duplicate destination id "${d.id}"`,
            });
        }
        seenDestIds.set(d.id, (prev ?? 0) + 1);
    }
    // Run registered validators — forward the full opts bag (with `deep`
    // normalized to a boolean) so downstream validators can read extra
    // keys like `sampleHeader`.
    const forwardedOpts = { ...opts, deep };
    for (const v of registered) {
        out.push(...v(content, forwardedOpts));
    }
    // Profile and recognition publish-gate validators — bridge ValidatorIssue →
    // ValidationError so they surface through the unified validateContent output.
    for (const issue of runProfileValidators(content)) {
        out.push({
            code: issue.code,
            severity: issue.severity,
            path: issue.path ? issue.path.split('.') : [],
            message: issue.message,
        });
    }
    if (content.formRecognition) {
        for (const issue of runRecognitionValidators(content.formRecognition)) {
            out.push({
                code: issue.code,
                severity: issue.severity,
                path: issue.path ? issue.path.split('.') : [],
                message: issue.message,
            });
        }
    }
    // Rule-attachment publish-gate validators
    out.push(...runRuleValidators(content));
    // Deep-only: compute.dependsOn cycles
    if (deep) {
        out.push(...detectComputeCycles(content));
    }
    return out;
}
function checkBehaviorRefs(b, f, stage, idx, fieldNames, out) {
    const basePath = ['fields', f.name, 'stageMap', stage, String(idx)];
    if (b.kind === 'compute') {
        for (const dep of b.dependsOn) {
            if (!fieldNames.has(dep)) {
                out.push({
                    code: 'behavior.ref.missing', severity: 'error',
                    path: [...basePath, 'dependsOn', dep],
                    message: `compute.dependsOn references field "${dep}" which is not in this form`,
                    fieldName: f.name,
                });
            }
        }
    }
    // parse.targets — each mapped target must reference a real field on the form
    if (b.kind === 'parse') {
        for (const [component, target] of Object.entries(b.targets)) {
            if (target && !fieldNames.has(target)) {
                out.push({
                    code: 'behavior.ref.missing', severity: 'error',
                    path: [...basePath, 'targets', component],
                    message: `parse.targets.${component} references field "${target}" which is not in this form`,
                    fieldName: f.name,
                });
            }
        }
    }
    // substitute.when condition walking — check field refs in condition args
    if (b.kind === 'substitute' && b.when) {
        walkConditionRefs(b.when, (ref) => {
            if (!fieldNames.has(ref)) {
                out.push({
                    code: 'behavior.ref.missing', severity: 'error',
                    path: [...basePath, 'when'],
                    message: `substitute.when references field "${ref}" which is not in this form`,
                    fieldName: f.name,
                });
            }
        });
    }
}
function walkConditionRefs(cond, visit) {
    if (!cond || typeof cond !== 'object')
        return;
    const c = cond;
    if (c.op === 'and' || c.op === 'or') {
        for (const sub of c.args ?? [])
            walkConditionRefs(sub, visit);
    }
    else {
        // leaf ops — first arg is usually a field ref when it's a string starting with "$"
        for (const a of c.args ?? []) {
            if (typeof a === 'string' && a.startsWith('$'))
                visit(a.slice(1));
        }
    }
}
function checkBehaviorCompleteness(b, f, stage, idx, out) {
    const basePath = ['fields', f.name, 'stageMap', stage, String(idx)];
    if (b.kind === 'populate') {
        const s = b.source;
        if (s.type === 'ocr-zone' && !s.zoneId) {
            out.push({ code: 'populate.source.incomplete', severity: 'error', path: basePath, message: 'ocr-zone requires a zoneId', fieldName: f.name });
        }
        if (s.type === 'header-xpath' && !('xpath' in s && s.xpath)) {
            out.push({ code: 'populate.source.incomplete', severity: 'error', path: basePath, message: 'header-xpath requires an xpath', fieldName: f.name });
        }
        if (s.type === 'sequence' && !('name' in s && s.name)) {
            out.push({ code: 'populate.source.incomplete', severity: 'error', path: basePath, message: 'sequence requires a name', fieldName: f.name });
        }
    }
    // Lane C Part 2 — behaviors no longer carry validation; the behavior-validate
    // completeness branch was removed. Validation completeness is enforced on the
    // derived path + RuleAttachment validators.
    if (b.kind === 'parse') {
        if (!b.parser) {
            out.push({ code: 'parse.incomplete', severity: 'error', path: basePath, message: 'parse requires a parser', fieldName: f.name });
        }
        if (b.editMode !== 'parts' && b.editMode !== 'composite') {
            out.push({ code: 'parse.incomplete', severity: 'error', path: basePath, message: 'parse requires editMode of parts or composite', fieldName: f.name });
        }
    }
}
/** Stages that are considered "boundary-only" and don't count as meaningful work stages. */
const BOUNDARY_STAGES = new Set(['import', 'export']);
/** Module-level set for O(1) required-stage lookups without per-call allocation. */
const REQUIRED_STAGE_SET = new Set(REQUIRED_STAGES);
/**
 * Run all profile-level publish-gate validators against the profiles array in
 * the given `JobTypeVersionContent`. Returns an empty array when `profiles` is
 * absent or empty (no profiles configured yet is a different concern).
 */
export function runProfileValidators(content) {
    const profiles = content.profiles;
    if (!profiles || profiles.length === 0)
        return [];
    const issues = [];
    // profile.exactly-one-fallback
    const fallbacks = profiles.filter((p) => p.isFallback);
    if (fallbacks.length !== 1) {
        issues.push({
            code: 'profile.exactly-one-fallback',
            severity: 'error',
            message: fallbacks.length === 0
                ? 'No fallback profile defined — exactly one profile must have isFallback = true'
                : `${fallbacks.length} fallback profiles found — exactly one is required`,
        });
    }
    // profile.unique-names
    const nameCounts = new Map();
    for (const p of profiles) {
        nameCounts.set(p.name, (nameCounts.get(p.name) ?? 0) + 1);
    }
    for (const [name, count] of nameCounts) {
        if (count > 1) {
            issues.push({
                code: 'profile.unique-names',
                severity: 'error',
                message: `Duplicate profile name "${name}"`,
                path: `profiles[name="${name}"]`,
            });
        }
    }
    for (const p of profiles) {
        const prefix = `profiles[id="${p.id}"]`;
        // profile.batch-fix-not-configurable
        if (p.stages.some((s) => s.stage === 'batch-fix')) {
            issues.push({
                code: 'profile.batch-fix-not-configurable',
                severity: 'error',
                message: `Profile "${p.name}" declares batch-fix in its stages — batch-fix is system-managed and may not be configured`,
                path: `${prefix}.stages`,
            });
        }
        // profile.enabled-stages-non-empty
        const hasNonBoundaryEnabled = p.stages.some((s) => s.enabled && !BOUNDARY_STAGES.has(s.stage));
        if (!hasNonBoundaryEnabled) {
            issues.push({
                code: 'profile.enabled-stages-non-empty',
                severity: 'error',
                message: `Profile "${p.name}" has no enabled stages beyond import/export`,
                path: `${prefix}.stages`,
            });
        }
        if (!p.isFallback) {
            // profile.predicate-non-empty — non-fallback profiles must have at least one clause
            if (p.predicate.clauses.length === 0) {
                issues.push({
                    code: 'profile.predicate-non-empty',
                    severity: 'error',
                    message: `Non-fallback profile "${p.name}" has no predicate clauses`,
                    path: `${prefix}.predicate.clauses`,
                });
            }
            // profile.clause-path-parses + profile.clause-value-required-for-op
            p.predicate.clauses.forEach((clause, idx) => {
                const clausePath = `${prefix}.predicate.clauses[${idx}]`;
                // Validate path syntax under declared format
                if (clause.format === 'xml') {
                    const result = tokenizeXPath(clause.path);
                    if ('error' in result) {
                        issues.push({
                            code: 'profile.clause-path-parses',
                            severity: 'error',
                            message: `Profile "${p.name}" clause ${idx}: invalid XPath "${clause.path}" — ${result.error}`,
                            path: `${clausePath}.path`,
                        });
                    }
                }
                else if (clause.format === 'json') {
                    const result = tokenizeJsonPath(clause.path);
                    if ('error' in result) {
                        issues.push({
                            code: 'profile.clause-path-parses',
                            severity: 'error',
                            message: `Profile "${p.name}" clause ${idx}: invalid JSON path "${clause.path}" — ${result.error}`,
                            path: `${clausePath}.path`,
                        });
                    }
                }
                // Validate that equals/not-equals have a value
                if ((clause.op === 'equals' || clause.op === 'not-equals') && clause.value == null) {
                    issues.push({
                        code: 'profile.clause-value-required-for-op',
                        severity: 'error',
                        message: `Profile "${p.name}" clause ${idx}: op "${clause.op}" requires a non-null value`,
                        path: `${clausePath}.value`,
                    });
                }
            });
        }
        else {
            // profile.fallback-not-starved — warn if fallback has no optional stages enabled
            const hasOptionalEnabled = p.stages.some((s) => s.enabled && !REQUIRED_STAGE_SET.has(s.stage));
            if (!hasOptionalEnabled) {
                issues.push({
                    code: 'profile.fallback-not-starved',
                    severity: 'warn',
                    message: `Fallback profile "${p.name}" has no optional stages enabled — this may indicate a misconfiguration`,
                    path: `${prefix}.stages`,
                });
            }
        }
    }
    return issues;
}
/**
 * Run recognition-level publish-gate validators.
 */
export function runRecognitionValidators(config) {
    const issues = [];
    if (config.confidenceThreshold < 0 || config.confidenceThreshold > 1) {
        issues.push({
            code: 'recognition.threshold-range',
            severity: 'error',
            message: `confidenceThreshold must be in [0, 1], got ${config.confidenceThreshold}`,
            path: 'formRecognition.confidenceThreshold',
        });
    }
    return issues;
}
// ---------------------------------------------------------------------------
// Publish-gate validators: rule attachments
// ---------------------------------------------------------------------------
/** Transform rule types that produce data (run before consumers). */
const TRANSFORM_TYPES = new Set(['substitute', 'format', 'calculate', 'copy', 'crossField']);
/** Consumer rule types that read/validate data (run after transforms). */
const CONSUMER_TYPES = new Set(['validate']);
/**
 * Run all rule-attachment publish-gate validators.
 * Exported for direct testing; also called from `validateContent`.
 */
export function runRuleValidators(content) {
    const rules = content.rules ?? [];
    if (rules.length === 0)
        return [];
    const out = [];
    // R-240: rule.fieldRef is the field internalName (engine truth — the batch gate
    // and revalidate resolve fieldRef against internalName-keyed maps). Key the
    // orphan check by name, not fieldDefinitionId, or every field-scope rule would
    // falsely orphan and block publish.
    const fieldNameSet = new Set(content.fields.map((f) => f.name));
    // 1. custom-script rejection
    for (const r of rules) {
        if (r.type === 'custom-script') {
            out.push({
                code: 'rule.custom-script-unavailable',
                severity: 'error',
                path: ['rules', r.id],
                message: `Rule "${r.name}" uses custom-script which is not available (V2)`,
            });
        }
    }
    // 2. Orphan fieldRef — fieldRef referencing non-existent field
    for (const r of rules) {
        if (r.scope === 'field' && r.fieldRef && !fieldNameSet.has(r.fieldRef)) {
            out.push({
                code: 'rule.orphan-fieldRef',
                severity: 'error',
                path: ['rules', r.id, 'fieldRef', r.fieldRef],
                message: `Rule "${r.name}" references field "${r.fieldRef}" which does not exist`,
            });
        }
    }
    // 2b. F-083 — active field-scope validate rules that can NEVER fire: no
    // resolvable target field (rule-runtime silently skips those), or neither a
    // condition nor a parameterized check (F-020's "rule with no rule", which
    // the drawer blocks but hand-edited / legacy / API-written content bypassed).
    // Inactive rules are skipped — deactivation is the deliberate escape hatch.
    for (const r of rules) {
        if (!r.isActive)
            continue;
        const finding = validateRuleCannotFire(r);
        if (finding) {
            out.push({
                code: 'rule.validate-cannot-fire',
                severity: 'error',
                path: ['rules', r.id],
                message: finding.message,
                linkTarget: { tab: 'rules' },
            });
        }
    }
    // 3. Dependency cycle detection — calculate/copy rules with sourceField/recalcOn
    const depGraph = new Map();
    for (const r of rules) {
        if (!r.isActive)
            continue;
        if (r.type !== 'calculate' && r.type !== 'copy')
            continue;
        const targets = r.fieldRef ? [r.fieldRef] : [];
        const sources = [];
        if (typeof r.config.sourceField === 'string')
            sources.push(r.config.sourceField);
        if (Array.isArray(r.config.recalcOn)) {
            for (const s of r.config.recalcOn) {
                if (typeof s === 'string')
                    sources.push(s);
            }
        }
        for (const target of targets) {
            if (!depGraph.has(target))
                depGraph.set(target, new Set());
            for (const src of sources)
                depGraph.get(target).add(src);
        }
    }
    // DFS cycle detection
    {
        const WHITE = 0, GRAY = 1, BLACK = 2;
        const color = new Map();
        for (const n of depGraph.keys())
            color.set(n, WHITE);
        function dfs(n) {
            color.set(n, GRAY);
            for (const dep of depGraph.get(n) ?? []) {
                const c = color.get(dep);
                if (c === GRAY)
                    return true;
                if ((c === undefined || c === WHITE) && dfs(dep))
                    return true;
            }
            color.set(n, BLACK);
            return false;
        }
        for (const n of depGraph.keys()) {
            if (color.get(n) === WHITE && dfs(n)) {
                out.push({
                    code: 'rule.dependency-cycle',
                    severity: 'error',
                    path: ['rules'],
                    message: `Rule dependency cycle detected involving field "${n}"`,
                });
            }
        }
    }
    // Only check active rules for duplicate / ordering warnings
    const active = rules.filter((r) => r.isActive);
    // 4. Duplicate detection — same (fieldRef, type, trigger, stage)
    {
        const seen = new Map();
        for (const r of active) {
            const fid = r.fieldRef ?? '__form__';
            const key = `${fid}|${r.type}|${r.trigger}|${r.stage}`;
            const prev = seen.get(key);
            if (prev) {
                out.push({
                    code: 'rule.duplicate',
                    severity: 'warn',
                    path: ['rules', r.id],
                    message: `Rule "${r.name}" duplicates (fieldRef=${fid}, type=${r.type}, trigger=${r.trigger}, stage=${r.stage}) with rule "${prev.name}"`,
                });
            }
            else {
                seen.set(key, r);
            }
        }
    }
    // 5. Dependency order — within (stage, trigger) group, transform rules should
    //    have lower displayOrder than consumer rules on the same fieldRef
    {
        // Group by (stage, trigger)
        const groups = new Map();
        for (const r of active) {
            const key = `${r.stage}|${r.trigger}`;
            if (!groups.has(key))
                groups.set(key, []);
            groups.get(key).push(r);
        }
        for (const group of groups.values()) {
            // For each fieldRef, find transform and consumer rules
            const fieldRules = new Map();
            for (const r of group) {
                if (!r.fieldRef)
                    continue;
                const fid = r.fieldRef;
                if (!fieldRules.has(fid))
                    fieldRules.set(fid, { transforms: [], consumers: [] });
                const bucket = fieldRules.get(fid);
                if (TRANSFORM_TYPES.has(r.type))
                    bucket.transforms.push(r);
                else if (CONSUMER_TYPES.has(r.type))
                    bucket.consumers.push(r);
            }
            for (const [fid, { transforms, consumers }] of fieldRules) {
                for (const consumer of consumers) {
                    for (const transform of transforms) {
                        if (transform.displayOrder > consumer.displayOrder) {
                            out.push({
                                code: 'rule.dependency-order',
                                severity: 'warn',
                                path: ['rules', transform.id],
                                message: `Transform rule "${transform.name}" (priority ${transform.displayOrder}) runs after consumer rule "${consumer.name}" (priority ${consumer.displayOrder}) on field "${fid}"`,
                            });
                        }
                    }
                }
            }
        }
    }
    return out;
}
function detectComputeCycles(content) {
    const out = [];
    const scopeOf = new Map();
    for (const f of content.fields)
        scopeOf.set(f.name, f.scope);
    const graph = new Map();
    for (const f of content.fields) {
        for (const stage of Object.keys(f.stageMap)) {
            for (const b of f.stageMap[stage] ?? []) {
                if (b.kind === 'compute') {
                    const sameScope = b.dependsOn.filter((dep) => scopeOf.get(dep) === f.scope);
                    graph.set(f.name, (graph.get(f.name) ?? []).concat(sameScope));
                }
            }
        }
    }
    const WHITE = 0, GRAY = 1, BLACK = 2;
    const color = new Map();
    for (const n of graph.keys())
        color.set(n, WHITE);
    function dfs(n) {
        color.set(n, GRAY);
        for (const dep of graph.get(n) ?? []) {
            const c = color.get(dep);
            if (c === GRAY)
                return true;
            if (c === WHITE && dfs(dep))
                return true;
        }
        color.set(n, BLACK);
        return false;
    }
    for (const n of graph.keys()) {
        if (color.get(n) === WHITE && dfs(n)) {
            out.push({
                code: 'compute.cycle', severity: 'error',
                path: ['fields', n], message: `compute.dependsOn cycle involving "${n}"`,
                fieldName: n,
            });
        }
    }
    return out;
}
//# sourceMappingURL=validators.js.map